import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError, formatDate, useApi } from '../api';
import { CopyButton } from '../components';

type Role = 'owner' | 'admin' | 'member';
interface Member {
  id: string;
  userId: string;
  email: string;
  name: string;
  role: Role;
  joinedAt: string;
  you: boolean;
}
interface Invite {
  id: string;
  email: string;
  role: Role;
  invitedBy: string | null;
  expiresAt: string;
}

const ROLE_HELP: Record<Role, string> = {
  owner: 'Everything, including billing and team roles',
  admin: 'Databases, billing and inviting members',
  member: 'Databases, data and API keys; no billing',
};

/** Team card on the Account page: members, pending invites, invite form. Free for every account. */
export function TeamCard({ myRole }: { myRole: Role }) {
  const team = useApi<{ members: Member[]; invites: Invite[] }>('/account/team');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [link, setLink] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const canManage = myRole === 'owner' || myRole === 'admin';
  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
      await team.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };
  return (
    <div className="card">
      <h2>Team</h2>
      <p className="muted">
        Share this account, its databases and its balance with colleagues. Free, up to 25 people.
      </p>
      <table>
        <thead>
          <tr>
            <th>Person</th>
            <th>Role</th>
            <th>Joined</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {team.data?.members.map((m) => (
            <tr key={m.id}>
              <td>
                {m.email} {m.you && <span className="muted">(you)</span>}
              </td>
              <td>
                {myRole === 'owner' && !m.you ? (
                  <select
                    value={m.role}
                    onChange={(e) =>
                      void run(() =>
                        api(`/account/team/members/${m.id}`, {
                          method: 'PATCH',
                          json: { role: e.target.value },
                        }),
                      )
                    }
                  >
                    {(['owner', 'admin', 'member'] as Role[]).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ) : (
                  m.role
                )}
              </td>
              <td className="muted">{formatDate(m.joinedAt)}</td>
              <td style={{ textAlign: 'right' }}>
                {(m.you || canManage) && (
                  <button
                    className="secondary"
                    onClick={() => {
                      if (!confirm(m.you ? 'Leave this account?' : `Remove ${m.email}?`)) return;
                      void run(async () => {
                        await api(`/account/team/members/${m.id}`, { method: 'DELETE' });
                        if (m.you) window.location.assign('/');
                      });
                    }}
                  >
                    {m.you ? 'Leave' : 'Remove'}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {team.data?.invites.map((i) => (
            <tr key={i.id} className="muted">
              <td>{i.email}</td>
              <td>{i.role} (invited)</td>
              <td>expires {formatDate(i.expiresAt)}</td>
              <td style={{ textAlign: 'right' }}>
                {canManage && (
                  <button
                    className="secondary"
                    onClick={() => void run(() => api(`/account/team/invites/${i.id}`, { method: 'DELETE' }))}
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form
          className="row"
          style={{ marginTop: 12, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const r = await api<{ link: string }>('/account/team/invites', {
                method: 'POST',
                json: { email, role },
              });
              setLink(r.link);
              setEmail('');
            });
          }}
        >
          <div className="field" style={{ flex: 1 }}>
            <label>Invite by email</label>
            <input
              type="email"
              required
              value={email}
              placeholder="colleague@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">member</option>
              <option value="admin">admin</option>
              {myRole === 'owner' && <option value="owner">owner</option>}
            </select>
          </div>
          <button type="submit">Send invite</button>
        </form>
      )}
      <p className="muted" style={{ marginTop: 6 }}>
        {ROLE_HELP[role]}
      </p>
      {link && (
        <div className="row" style={{ marginTop: 8 }}>
          <div className="secret" style={{ flex: 1, borderStyle: 'solid' }}>
            {link}
          </div>
          <CopyButton text={link} />
          <span className="muted">Emailed too. Valid 7 days.</span>
        </div>
      )}
      {err && <p className="error">{err}</p>}
    </div>
  );
}

/** Sidebar switcher shown when the user belongs to more than one account. */
export function AccountSwitcher() {
  const ms = useApi<{ accounts: { accountId: string; name: string; role: Role; current: boolean }[] }>(
    '/account/memberships',
  );
  const list = ms.data?.accounts ?? [];
  if (list.length < 2) return null;
  return (
    <select
      aria-label="Account"
      style={{ margin: '0 12px 12px' }}
      value={list.find((a) => a.current)?.accountId ?? ''}
      onChange={(e) =>
        void api('/account/switch', { method: 'POST', json: { accountId: e.target.value } }).then(() =>
          window.location.assign('/'),
        )
      }
    >
      {list.map((a) => (
        <option key={a.accountId} value={a.accountId}>
          {a.name} ({a.role})
        </option>
      ))}
    </select>
  );
}

/** /invite/:token — shown to a signed-in user who followed an invitation link. */
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; accountName: string; email: string; role: Role }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  useEffect(() => {
    api<{ accountName: string; email: string; role: Role }>(`/invites/${token}`)
      .then((r) => setState({ kind: 'ready', ...r }))
      .catch((e: unknown) =>
        setState({ kind: 'error', message: e instanceof ApiError ? e.message : 'Invitation not found' }),
      );
  }, [token]);
  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <h1>Team invitation</h1>
      {state.kind === 'loading' && <p className="muted">Checking your invitation…</p>}
      {state.kind === 'error' && <p className="error">{state.message}</p>}
      {state.kind === 'ready' && (
        <>
          <p>
            You have been invited to join <strong>{state.accountName}</strong> as{' '}
            <strong>{state.role}</strong>. The invitation is for {state.email}; you must be signed in with
            that address.
          </p>
          <div className="row">
            <button
              onClick={() =>
                void api(`/invites/${token}/accept`, { method: 'POST' })
                  .then(() => window.location.assign('/'))
                  .catch((e: unknown) =>
                    setState({
                      kind: 'error',
                      message: e instanceof ApiError ? e.message : 'Could not accept',
                    }),
                  )
              }
            >
              Join {state.accountName}
            </button>
            <button className="secondary" onClick={() => navigate('/')}>
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
